import queryInfoList from "services/queryInfoList";
import queryNoteMetaDataMinimals from "services/queryNoteMetaDataMinimals";
import querySecureData from "services/querySecureData";
import fetchAllPages from "utils/fetch-all-pages";

export type PatientNote = NoteMetaDataMinimal & NoteDataInfo;
export type PatientNoteWithSecureData = PatientNote & {
  secureData: NoteData | undefined;
};

export const mergeMinimalsAndDataInfos = ({
  minimals,
  dataInfos,
}: {
  minimals: NoteMetaDataMinimal[];
  dataInfos: NoteDataInfo[];
}): PatientNote[] =>
  minimals.map((el) => {
    // might exist several notes with different revision
    const notesSortedByRevision = dataInfos
      .filter((each) => each.note_data_id === el.note_data_id)
      .sort((a, b) => (a.note_revision > b.note_revision ? -1 : 1));

    return {
      ...el,
      ...notesSortedByRevision[0],
    };
  });

export const queryPatientNotes = ({
  partner_cognito_sub,
  ...arg
}: (
  | Parameters<typeof queryNoteMetaDataMinimals>[0]
  | Parameters<typeof queryInfoList>[0]
) & {
  partner_cognito_sub?: Parameters<
    typeof queryInfoList
  >[0]["patient_cognito_sub"];
}): Promise<PatientNote[]> =>
  Promise.all([
    queryNoteMetaDataMinimals({ partner_cognito_sub, ...arg }),
    queryInfoList({
      patient_cognito_sub: partner_cognito_sub,
      ...arg,
    }),
  ]).then(([noteMetaDataMinimals, noteDataInfos]) =>
    mergeMinimalsAndDataInfos({
      minimals: noteMetaDataMinimals,
      dataInfos: noteDataInfos,
    }),
  );

export const queryPatientNotesAll = ({
  partner_cognito_sub,
  ...arg
}: (
  | Parameters<typeof queryNoteMetaDataMinimals>[0]
  | Parameters<typeof queryInfoList>[0]
) & {
  partner_cognito_sub?: Parameters<
    typeof queryInfoList
  >[0]["patient_cognito_sub"];
}): Promise<PatientNote[]> =>
  Promise.all([
    fetchAllPages(queryNoteMetaDataMinimals, { partner_cognito_sub, ...arg }),
    fetchAllPages(queryInfoList, {
      patient_cognito_sub: partner_cognito_sub,
      ...arg,
    }),
  ]).then(([noteMetaDataMinimals, noteDataInfos]) =>
    mergeMinimalsAndDataInfos({
      minimals: noteMetaDataMinimals,
      dataInfos: noteDataInfos,
    }),
  );

export const querySecureDataForPatientNotes = async (
  notes: PatientNote[],
): Promise<PatientNoteWithSecureData[]> =>
  Promise.all(
    notes.map(async (el) => {
      let secureData: NoteData | undefined = undefined;

      try {
        secureData = await querySecureData({
          type: "note",
          id: el.note_data_id,
        });
      } catch (error) {
        // swallow this error
      }

      return {
        ...el,
        secureData,
      } as PatientNoteWithSecureData;
    }),
  );

export const queryPatientNotesWithSecureData = (
  arg: (
    | Parameters<typeof queryNoteMetaDataMinimals>[0]
    | Parameters<typeof queryInfoList>[0]
  ) & {
    partner_cognito_sub?: Parameters<
      typeof queryInfoList
    >[0]["patient_cognito_sub"];
  },
) => queryPatientNotes(arg).then(querySecureDataForPatientNotes);

export const queryPatientNotesWithSecureDataAll = (
  arg: (
    | Parameters<typeof queryNoteMetaDataMinimals>[0]
    | Parameters<typeof queryInfoList>[0]
  ) & {
    partner_cognito_sub?: Parameters<
      typeof queryInfoList
    >[0]["patient_cognito_sub"];
  },
) => queryPatientNotesAll(arg).then(querySecureDataForPatientNotes);
